import * as EventTracker from '@src/analytics/EventTracker';
import { saveLightweightReelFromStory } from '@src/services/AIClipProducer';
import { reelPlaceholderTitle } from '@src/services/reel/reelPlaceholderTitle';
import { useContentStudioUI } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useRef, useState } from 'react';

import { clipFragmentsFromClips } from './clipFragmentsFromClips';
import { useOpenClipPreview } from './useOpenClipPreview';

import type { OtherVideo } from '@src/types.ts/story';

const logger = LogManager.getLogger('content:useSaveClipsAsReel');

/** Two or more, per PLAT-11049: a single clip is already a reel of one Section. */
export const MIN_CLIPS_FOR_REEL = 2;

type UseSaveClipsAsReelReturn = {
  /** Resolves `true` when the reel was saved, so the caller can clear its selection. */
  saveAsReel: (clips: OtherVideo[]) => Promise<boolean>;
  /**
   * PLAT-11249 — the explicit Clip-to-Reel action, from the clip preview's
   * Reel Editor tab: one clip becomes a reel of one Section, which is then
   * opened and treated as the Reel it now is. The two-clip minimum is
   * `saveAsReel`'s (combining fewer than two is a no-op *combine*); a
   * conversion is exactly the one-clip case, made deliberate.
   */
  createReelFromClip: (clip: OtherVideo) => Promise<boolean>;
  isSaving: boolean;
};

/**
 * Combines the selected Video Clips into one reel, in the order given
 * (PLAT-11049, epic DE11).
 *
 * Saves through `saveLightweightReelFromStory` rather than `saveProducedClips`:
 * the latter begins by reading `videoCreator.currentVideo`, which Content Studio
 * never loads, so it throws immediately from this surface. The reel is written
 * as a lightweight clip carrying `subClips` in selection order and re-cropped
 * from them on open, exactly as a Reel Producer reel is.
 */
export function useSaveClipsAsReel(): UseSaveClipsAsReelReturn {
  const videoCreator = useVideoCreatorStore();
  const contentStudioUI = useContentStudioUI();
  const { open: openClipPreview } = useOpenClipPreview();

  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);

  /** The save both entry points share: fragments in, the reel opened, toasts
      either way. Callers have already guarded `savingRef` and the fragment
      minimum their gesture calls for. */
  const saveReelFromFragments = useCallback(
    async (
      fragments: ReturnType<typeof clipFragmentsFromClips>,
      meta: {
        clipsSelected: number;
        source: 'video_clips' | 'clip_preview';
        successMessage: string;
      },
    ): Promise<boolean> => {
      savingRef.current = true;
      setIsSaving(true);
      try {
        logger.info('saveAsReel: starting', {
          clipCount: meta.clipsSelected,
          fragmentCount: fragments.length,
          source: meta.source,
          storyId: videoCreator.story?.id,
        });

        const title = reelPlaceholderTitle(
          videoCreator.story?.title ?? 'Story',
        );
        const reel = await saveLightweightReelFromStory(
          videoCreator,
          fragments,
          title,
        );

        if (!reel?.id) {
          // Already logged with its reason inside the service.
          videoCreator.showToast({
            state: 'error',
            message: 'Could not create the reel. Please try again.',
          });
          return false;
        }

        // PLAT-11063 — after the id is confirmed, so a failed save is not
        // counted as a reel that got made (DS2).
        EventTracker.trackReelCreatedFromClips({
          story_id: videoCreator.story?.id,
          clips_selected: meta.clipsSelected,
          sections_resolved: fragments.length,
          source: meta.source,
        });

        contentStudioUI.markClipsSaved([reel.id]);
        await openClipPreview(reel);

        videoCreator.showToast({
          state: 'success',
          message: meta.successMessage,
        });
        logger.info('saveAsReel: complete', { videoId: reel.id });
        return true;
      } catch (error) {
        // The selection is deliberately left intact so the creator can retry
        // without re-picking what they just picked.
        logger.error('saveAsReel: failed', { error: extractErrorInfo(error) });
        videoCreator.showToast({
          state: 'error',
          message: 'Could not create the reel. Please try again.',
        });
        return false;
      } finally {
        savingRef.current = false;
        setIsSaving(false);
      }
    },
    [videoCreator, contentStudioUI, openClipPreview],
  );

  const saveAsReel = useCallback(
    async (clips: OtherVideo[]): Promise<boolean> => {
      if (savingRef.current) return false;

      const fragments = clipFragmentsFromClips(clips);
      if (fragments.length < MIN_CLIPS_FOR_REEL) {
        logger.warn('saveAsReel: not enough usable clips', {
          selected: clips.length,
          resolved: fragments.length,
        });
        videoCreator.showToast({
          state: 'error',
          message: 'Select at least two clips that have a source range.',
        });
        return false;
      }

      return saveReelFromFragments(fragments, {
        clipsSelected: clips.length,
        source: 'video_clips',
        successMessage: `Reel created from ${fragments.length} clips.`,
      });
    },
    [videoCreator, saveReelFromFragments],
  );

  const createReelFromClip = useCallback(
    async (clip: OtherVideo): Promise<boolean> => {
      if (savingRef.current) return false;

      const fragments = clipFragmentsFromClips([clip]);
      if (fragments.length === 0) {
        logger.warn('createReelFromClip: clip has no source range', {
          clipId: clip.id,
        });
        videoCreator.showToast({
          state: 'error',
          message: 'This clip has no source range to build a reel from.',
        });
        return false;
      }

      return saveReelFromFragments(fragments, {
        clipsSelected: 1,
        source: 'clip_preview',
        successMessage: 'Reel created from this clip.',
      });
    },
    [videoCreator, saveReelFromFragments],
  );

  return { saveAsReel, createReelFromClip, isSaving };
}
